import { FormEvent, useEffect, useState } from 'react';
import type { DebtPaymentMethod, RegisterRef } from '@kassa/shared';
import { api, ApiError } from '../api';
import { formatMoney } from '../format';
import { Receipt } from './Receipt';
import { useConfirm } from '../components/ConfirmDialog';

interface Props {
  customerId: string;
  balance: number;
  onPaid: () => void;
  /** Açıq növbənin kassası məlumdursa (kassa ekranı): kassa seçimi göstərilmir. */
  registerId?: string;
}

// Nisyə borcunun kassada ödənişi. UUID bir dəfə yaranır: təkrar klik ikinci ödəniş yaratmır. Borcdan çox ödəniş təsdiq istəyir.
export function DebtPayment({ customerId, balance, onPaid, registerId: fixedRegisterId }: Props) {
  const confirm = useConfirm();
  const [registers, setRegisters] = useState<RegisterRef[]>([]);
  const [registerId, setRegisterId] = useState(fixedRegisterId ?? '');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<DebtPaymentMethod>('cash');
  const [note, setNote] = useState('');
  const [payId, setPayId] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [receiptId, setReceiptId] = useState<string | null>(null);

  useEffect(() => {
    if (fixedRegisterId) return;
    api
      .shiftRegisters()
      .then((r) => {
        setRegisters(r);
        if (r.length > 0) setRegisterId(r[0].id);
      })
      .catch(() => setRegisters([]));
  }, [fixedRegisterId]);

  async function send(overpayConfirmed: boolean) {
    setBusy(true);
    setError(null);
    try {
      const r = await api.debtPay(customerId, {
        id: payId,
        registerId,
        amount: Number(amount),
        method,
        note: note.trim() || null,
        overpayConfirmed,
      });
      setResult(`${r.number}: ${formatMoney(r.amount)} — borc ${formatMoney(r.balanceBefore)} → ${formatMoney(r.balanceAfter)}`);
      setReceiptId(r.id);
      setAmount('');
      setNote('');
      setPayId(crypto.randomUUID());
      onPaid();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'overpay' && !overpayConfirmed) {
        if (await confirm(`${err.message}. Davam edilsin?`, { confirmLabel: 'Davam et' })) {
          setBusy(false);
          return send(true);
        }
      } else {
        setError(err instanceof ApiError ? err.message : 'Ödəniş alınmadı. Yenidən cəhd edin.');
      }
    } finally {
      setBusy(false);
    }
  }

  const submit = (e: FormEvent) => {
    e.preventDefault();
    send(false);
  };

  return (
    <section className="card">
      <h2>Borc ödənişi</h2>
      <p>
        Cari borc: <strong className={balance > 0 ? 'bad' : ''}>{formatMoney(balance)}</strong>
        {balance < 0 && <span className="muted"> (avans)</span>}
      </p>
      {!fixedRegisterId && registers.length === 0 && <p className="muted">Kassa seçilməyib və ya açıq növbə yoxdur.</p>}
      <form onSubmit={submit} className="form-grid">
        {!fixedRegisterId && (
          <label>
            Kassa
            <select value={registerId} onChange={(e) => setRegisterId(e.target.value)} disabled={busy || registers.length === 0}>
              {registers.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.outletName} · {r.code} {r.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          Məbləğ
          <input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required />
        </label>
        <label>
          Üsul
          <select value={method} onChange={(e) => setMethod(e.target.value as DebtPaymentMethod)}>
            <option value="cash">Nağd</option>
            <option value="card">Kart</option>
          </select>
        </label>
        <label>
          Qeyd
          <input type="text" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
        </label>
        <button type="submit" className="btn-primary" disabled={busy || !registerId || !amount}>
          Ödənişi qəbul et
        </button>
      </form>
      {result && <p className="ok" aria-live="polite">{result}</p>}
      {receiptId && <Receipt id={receiptId} />}
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
